import EmbeddableCheckbox from "@components/C138_EmbeddableForm/EmbeddableCheckbox";
import Grid from "@components/Grid/Grid";
import Link from "@components/Link/Link";
import StructuredData from "@components/StructuredData/StructuredData";
import { CareerStagesDE } from "@customTypes/careerStagesDE";
import { CareerStagesEE } from "@customTypes/careerStagesEE";
import { CareerStagesFormat } from "@customTypes/careerStagesFormat";
import { CareerStagesTopic } from "@customTypes/careerStagesTopic";
import { FilterType } from "@customTypes/FilterType";
import { ProgrammeDetails } from "@customTypes/programmeDetails";
import { ProgrammeType } from "@customTypes/ProgrammeType";
import { useSettings } from "@utilities/context/settings";
import { fetchApi } from "@utilities/fetchApi";
import { filterProgrammesByDateAsc } from "@utilities/filterProgrammesByDateAsc";
import slugify from "@utilities/slugify";
import clsx from "clsx";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import styles from "./C265_ProgrammesByCareerStage.module.scss";

import "swiper/css";
import { A11y, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { ITEMS_PER_SLIDE } from "../../constants";

// new swiper settings
import { Swiper as SwiperProps } from "swiper/types";

import SwiperNavigation from "@components/Swiper/SwiperNavigation";
import SwiperPagination from "@components/Swiper/SwiperPagination";
import { useDevice } from "@utilities/react/get-device/get-device";
import "swiper/scss/a11y";

//End of new swiper settings

const AccordionContainer = dynamic(
  () => import("@components/C11_VerticalAccordion/AccordionContainer")
);

const generateCourseListSchema = (programmes: ProgrammeDetails[]) => {
  return {
    "@type": "ItemList",
    itemListElement: programmes.map((programme, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${process.env.NEXT_PUBLIC_URL}${
        programme.slug?.startsWith("/") ? programme.slug : "/" + programme.slug
      }`,
    })),
  };
};

export interface ProgrammesByCareerStageProps {
  heading1?: string;
  heading2?: string;
  introText?: string;
  programmeType: ProgrammeType;
  programmeAttribute: any;
  useBlueCssClass: boolean;
  tags: Array<string>;
  filterType?: FilterType;
  isCarousel?: boolean;
}

const NUMBER_OF_COLUMNS = 3;

type FilterGroupKey = "experience" | "topic" | "format" | "de";
type SelectedFiltersByGroup = Record<FilterGroupKey, Set<string>>;

export const ProgrammesByCareerStage = (props: {
  content: ProgrammesByCareerStageProps;
}) => {
  const { content } = props;
  const {
    heading1,
    heading2,
    introText,
    programmeType,
    useBlueCssClass,
    tags,
    filterType,
    isCarousel,
  } = content;

  const [swiper, setSwiper] = useState<SwiperProps>();
  const [currentPage, setCurrentPage] = useState(1);
  const device = useDevice();

  const updateSlidesInert = (swiper: SwiperProps) => {
    swiper.slides.forEach((slideEl, index) => {
      const isActive = index === swiper.activeIndex;

      if (isActive) {
        slideEl.removeAttribute("inert");
      } else {
        slideEl.setAttribute("inert", "true");
      }
    });
  };

  const swiperParams = {
    className: clsx(styles.swiper),
    spaceBetween: 16,
    modules: [Keyboard, A11y],
    onInit: (swiper: SwiperProps) => {
      updateSlidesInert(swiper);
    },
    slidesPerView: 1,
    slidesPerGroup: 1,
    loopAddBlankSlides: true,
    rewind: true,
    enabled: true,
    keyboard: { enabled: true, onlyInViewport: true },
    a11y: { enabled: true },
    onSwiper: (swiper: SwiperProps) => {
      setSwiper(swiper);
    },
    onSlideChange: (swiper: SwiperProps) => {
      updateSlidesInert(swiper);
      setCurrentPage(swiper.realIndex + 1);
    },
  };

  const { allTags } = useSettings();

  const useBlueCssClassExcpression = useBlueCssClass ? "blue" : "";
  const { programmeSettings } = useSettings();

  const [selectedFilters, setSelectedFilters] =
    useState<SelectedFiltersByGroup>({
      experience: new Set(),
      topic: new Set(),
      format: new Set(),
      de: new Set(),
    });

  const hasAnySelectedFilters = Object.values(selectedFilters).some(
    (set) => set.size > 0
  );
  const [programmeDetails, setProgrammeDetails] = useState<ProgrammeDetails[]>(
    []
  );
  const [experienceFilters, setExperienceFilters] = useState<string[]>([]);
  const [topicFilters, setTopicFilters] = useState<string[]>([]);
  const [formatFilters, setFormatFilters] = useState<string[]>([]);
  const [deFilters, setDeFilters] = useState<string[]>([]);
  const selectedFilterTypes =
    Array.isArray(filterType) && filterType.length > 0
      ? filterType
      : [FilterType.ProgrammeCareerStagesExperienceEE]; // Default to Experience

  const isExperienceFilterEnabled = selectedFilterTypes.includes(
    FilterType.ProgrammeCareerStagesExperienceEE
  );
  const isTopicFilterEnabled = selectedFilterTypes.includes(
    FilterType.ProgrammeCareerStagesTopic
  );
  const isFormatFilterEnabled = selectedFilterTypes.includes(
    FilterType.ProgrammeCareerStagesFormat
  );

  const isEEtype = programmeType === ProgrammeType.EE;
  const supportsCompare =
    programmeType === ProgrammeType.EE || programmeType === ProgrammeType.DE;

  // Get the collection of name id pair based on the tags on the page
  function getTagName(
    pageTags: Array<string>,
    allTags: Record<string, string> = {}
  ) {
    return pageTags?.map((id) => ({ name: allTags[id], id }));
  }

  const c265topics = getTagName(tags, allTags);

  // OR within each group, AND across groups
  const programmeMatchesAnyFilters = ({
    programmeCareerStagesEeList,
    programmeCareerStagesTopic,
    programmeCareerStagesFormat,
    programmeCareerStagesDeList,
  }: ProgrammeDetails) => {
    if (!hasAnySelectedFilters) return true;

    if (!isEEtype) {
      // DE only (single group => OR)
      const programmeTags = new Set(
        (programmeCareerStagesDeList || []).map((tag) => slugify(tag))
      );

      const selected = selectedFilters.de;
      if (selected.size === 0) return true;

      return [...selected].some((filter) => programmeTags.has(filter));
    }

    // EE (AND across groups)
    const tagsByGroup: Record<Exclude<FilterGroupKey, "de">, Set<string>> = {
      experience: new Set(
        (programmeCareerStagesEeList || []).map((tag) => slugify(tag))
      ),
      topic: new Set(
        (programmeCareerStagesTopic || []).map((tag) => slugify(tag))
      ),
      format: new Set(
        (programmeCareerStagesFormat || []).map((tag) => slugify(tag))
      ),
    };

    const groups: Array<Exclude<FilterGroupKey, "de">> = [
      "experience",
      "topic",
      "format",
    ];

    return groups.every((group) => {
      const selected = selectedFilters[group];
      if (selected.size === 0) return true; // group not used => ignore
      const programmeTags = tagsByGroup[group];
      return [...selected].some((filter) => programmeTags.has(filter)); // OR within group
    });
  };

  const handleFilterChange = (group: FilterGroupKey) => (event) => {
    const { value, checked } = event.target;

    setSelectedFilters((prev) => {
      const next = { ...prev };
      const groupSet = new Set(next[group]);

      if (checked) {
        groupSet.add(value);
      } else {
        groupSet.delete(value);
      }
      next[group] = groupSet;
      return next;
    });
  };

  const noItemsFound = programmeDetails.every(
    (d) => !programmeMatchesAnyFilters(d)
  );

  const getProgrammeColumns = (programmes: ProgrammeDetails[]) => {
    const columns: ProgrammeDetails[][] = new Array(NUMBER_OF_COLUMNS)
      .fill(null)
      .map(() => []);

    programmes
      .sort((a, b) => {
        const programmeFeeA = parseInt(
          (a.programmeImportData
            ? a.programmeImportData[0].programmeFee?.replace(/\D/g, "")
            : "") || "",
          10
        );
        const programmeFeeB = parseInt(
          (b.programmeImportData
            ? b.programmeImportData[0].programmeFee?.replace(/\D/g, "")
            : "") || "",
          10
        );

        if (!programmeFeeA || !programmeFeeB) return 0;

        if (programmeFeeA < programmeFeeB) {
          return 1;
        }
        if (programmeFeeA > programmeFeeB) {
          return -1;
        }

        return 0;
      })
      .forEach((programme, i) =>
        columns[i % NUMBER_OF_COLUMNS].push(programme)
      );

    return columns;
  };

  const getProgrammeMarkup = (programmes: ProgrammeDetails[]) =>
    programmes.map((programme) => (
      <AccordionContainer
        key={programme.programmeTitle}
        noHeading
        isProgramme
        isCompare={supportsCompare}
        programme={programme}
        hasBlueBackground={useBlueCssClass}
        customClass={clsx(
          styles["vertical-accordion"],
          !programmeMatchesAnyFilters(programme) &&
            hasAnySelectedFilters &&
            styles["hide"],
          isCarousel && styles["carousel-layout"]
        )}
        accordionFields={{
          accordionItems: [{ fields: { title: null, content: null } }],
          singleDisplay: false,
          defaultOpen: false,
          title: programme?.programmeTitle || "",
        }}
      />
    ));

  const getErrorMessage = () => (
    <div className={styles.error}>
      We haven&apos;t got any exact matches for your search at the moment.
      Please use our{" "}
      <Link href={programmeSettings.programmeFinderLink || ""}>
        programme finder
      </Link>
      to browse all of our executive education programmes
    </div>
  );

  useEffect(() => {
    const getData = async () => {
      const programmeDetails = await fetchApi(
        "/api/getProgrammesByCareerStageData",
        {
          programmeType,
        }
      );

      const filteredMap = new Map<string, ProgrammeDetails>();

      c265topics?.forEach((topic) => {
        programmeDetails.forEach((courseTopic) => {
          const attrs = courseTopic?.programmeAttribute || [];
          if (attrs.includes(topic.name)) {
            if (courseTopic.slug)
              filteredMap.set(courseTopic.slug, courseTopic);
          }
        });
      });

      setProgrammeDetails([...filteredMap.values()]);
    };
    getData();
  }, []);

  useEffect(() => {
    if (programmeDetails.length > 0) {
      let CareerStagesExperienceFilters: CareerStagesEE[] = [];
      let CareerStagesTopicFilters: CareerStagesTopic[] = [];
      let CareerStagesFormatFilters: CareerStagesFormat[] = [];
      let CareerStagesDEFilters: CareerStagesDE[] = [];

      if (isEEtype) {
        CareerStagesExperienceFilters = programmeDetails
          .flatMap(
            ({ programmeCareerStagesEeList }) =>
              programmeCareerStagesEeList || []
          )
          .filter((x): x is CareerStagesEE => Boolean(x));

        CareerStagesTopicFilters = programmeDetails
          .flatMap(
            ({ programmeCareerStagesTopic }) => programmeCareerStagesTopic || []
          )
          .filter((x): x is CareerStagesTopic => Boolean(x));

        CareerStagesFormatFilters = programmeDetails
          .flatMap(
            ({ programmeCareerStagesFormat }) =>
              programmeCareerStagesFormat || []
          )
          .filter((x): x is CareerStagesFormat => Boolean(x));
      } else {
        CareerStagesDEFilters = programmeDetails
          .flatMap(
            ({ programmeCareerStagesDeList }) =>
              programmeCareerStagesDeList || []
          )
          .filter((x): x is CareerStagesDE => Boolean(x));
      }
      setExperienceFilters([...new Set(CareerStagesExperienceFilters)].sort());
      setTopicFilters([...new Set(CareerStagesTopicFilters)].sort());
      setFormatFilters([...new Set(CareerStagesFormatFilters)].sort());
      setDeFilters([...new Set(CareerStagesDEFilters)].sort());
    }
  }, [programmeDetails]);

  const renderCheckboxes = (
    filters: string[],
    group: FilterGroupKey,
    isDE = false
  ) => {
    if (!filters || filters.length === 0) return null;

    if (isDE) {
      return (
        <div className="form-row full">
          {filters.map((filter) => {
            const slug = slugify(filter);
            const checkboxId = `c265-${slug}`; // unique DOM id

            return (
              <EmbeddableCheckbox
                label={filter || ""}
                key={checkboxId}
                id={checkboxId}
                value={slug}
                onChange={handleFilterChange(group)}
              />
            );
          })}
        </div>
      );
    }

    return (
      <div className={clsx("component", "form", styles.form)}>
        {filters.map((filter, i) => {
          const slug = slugify(filter);
          const checkboxId = `c265-${slug}`; // unique DOM id

          return (
            <div className="form-row" key={filter + i}>
              <EmbeddableCheckbox
                label={filter || ""}
                id={checkboxId}
                value={slug}
                onChange={handleFilterChange(group)}
              />
            </div>
          );
        })}
      </div>
    );
  };
  // Only filters selected in Contentful are shown
  const filterOptions = [
    isTopicFilterEnabled && {
      label: "Filter by Topic",
      filters: topicFilters,
      group: "topic" as const,
    },
    isExperienceFilterEnabled && {
      label: "Filter by Experience",
      filters: experienceFilters,
      group: "experience" as const,
    },
    isFormatFilterEnabled && {
      label: "Filter by Location/Format",
      filters: formatFilters,
      group: "format" as const,
    },
  ].filter(Boolean); // Remove false values

  const shouldRenderStructuredData = programmeDetails.length > 0;

  function chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  const filteredProgrammes = programmeDetails.filter((programme) =>
    programmeMatchesAnyFilters(programme)
  );

  const itemsPerSlide = device === "mobile" ? 3 : ITEMS_PER_SLIDE;

  const hasMoreThanOneSlide = filteredProgrammes.length > itemsPerSlide;

  const slidesData = chunkArray(filteredProgrammes, itemsPerSlide);

  useEffect(() => {
    if (swiper && hasAnySelectedFilters) {
      swiper.slideTo(0);
      setCurrentPage(1);
    }
  }, [selectedFilters, swiper]);

  return (
    <>
      {shouldRenderStructuredData && (
        <>
          <StructuredData
            id="courseListSchema"
            schemaData={generateCourseListSchema(programmeDetails)}
          />
        </>
      )}
      <div className={clsx("outer-wrap", useBlueCssClassExcpression)}>
        <Grid row>
          <Grid column sm={12}>
            <div
              className={clsx(
                "component",
                "programmes-by-career-stage",
                supportsCompare
                  ? styles["programmes-by-career-stage-comparison"]
                  : styles["programmes-by-career-stage"],
                "wrapper",
                useBlueCssClassExcpression
              )}
            >
              {heading1 && (
                <div className={styles["top-heading"]}>
                  <span className={styles["section-title"]}>{heading1}</span>
                </div>
              )}
              <div className={styles["title-with-intro"]}>
                {heading2 && (
                  <div className={styles["left-section"]}>
                    <h2>{heading2}</h2>
                  </div>
                )}
                <div className={styles["right-section"]}>
                  {introText && <p>{introText}</p>}
                </div>
              </div>
              {isEEtype && (
                <div
                  className={clsx(
                    styles["filters-container"],
                    isCarousel && styles["carousel-layout"]
                  )}
                >
                  {filterOptions.map(({ label, filters, group }) =>
                    filters.length > 0 ? (
                      <AccordionContainer
                        key={label}
                        noHeading
                        isDropdown
                        customClass={styles.dropdown}
                        accordionFields={{
                          singleDisplay: false,
                          defaultOpen: false,
                          accordionItems: [
                            {
                              fields: {
                                title: (
                                  <>
                                    <span>{label}</span>
                                    <span className="chevron"></span>
                                  </>
                                ),
                                content: renderCheckboxes(filters, group),
                              },
                              uid: slugify(label),
                            },
                          ],
                          title: label,
                        }}
                      />
                    ) : null
                  )}
                  {hasAnySelectedFilters && noItemsFound && getErrorMessage()}
                </div>
              )}

              {!isEEtype && (
                <Grid
                  row
                  customClass={clsx("outer-wrap", useBlueCssClassExcpression)}
                >
                  <fieldset
                    className={clsx(styles.filters, styles.form, "form")}
                  >
                    <legend className={styles["options-label"]}>
                      {programmeSettings.programmeCareerStageDropDownLabel}
                    </legend>
                    {!isEEtype && renderCheckboxes(deFilters, "de", true)}
                  </fieldset>
                  {hasAnySelectedFilters && noItemsFound && getErrorMessage()}
                </Grid>
              )}

              {!isCarousel ? (
                <div className={styles["filtered-content"]}>
                  {getProgrammeColumns(programmeDetails).map(
                    (programmeColumn, i) => (
                      <div key={"column-" + i} className={styles["group-col"]}>
                        {getProgrammeMarkup(programmeColumn)}
                      </div>
                    )
                  )}
                </div>
              ) : hasMoreThanOneSlide ? (
                <>
                  <div className={styles["carousel-wrapper"]}>
                    <Swiper {...swiperParams}>
                      {slidesData.map((slideItems, index) => (
                        <SwiperSlide key={"slide-" + index}>
                          <div
                            className={clsx(
                              styles["filtered-content"],
                              styles["carousel-layout"]
                            )}
                          >
                            {getProgrammeMarkup(
                              filterProgrammesByDateAsc(slideItems)
                            )}
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>

                    {hasMoreThanOneSlide && (
                      <>
                        <SwiperPagination
                          currentPage={currentPage}
                          totalPages={slidesData.length}
                          swiper={swiper}
                        />

                        <SwiperNavigation
                          className={styles.next}
                          swiper={swiper}
                          direction="next"
                          perPage={1}
                          variation={
                            device === "desktop-large" ? "fraction" : "arrow"
                          }
                          currentPage={currentPage}
                          totalFilteredPages={slidesData.length}
                        />
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div
                  className={
                    styles["filtered-content"] + " " + styles["carousel-layout"]
                  }
                >
                  {getProgrammeMarkup(
                    filterProgrammesByDateAsc(programmeDetails)
                  )}
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default ProgrammesByCareerStage;
